import { Injectable } from '@angular/core';
import { resolveBibleId } from 'supabase/functions/_shared/bibles';
import {
  DEFAULT_LLM_MODEL,
  isValidLlmModel,
  VeniceLlmModel,
} from 'supabase/functions/chat/models';
import {
  ImageSize,
  resolveModel,
  VeniceImageModel,
} from 'supabase/functions/venice/models';
import {
  ColorSchemePreference,
  isColorSchemePreference,
} from '../theme/color-scheme';

export interface StorageSchema {
  'selected-bible-id': string;
  'selected-public-bible-id': string;
  'preferred-venice-model': VeniceImageModel;
  'preferred-image-size': ImageSize;
  'preferred-llm-model': VeniceLlmModel;
  'preferred-color-scheme': ColorSchemePreference;
  'ai-consent': 'true';
  'copyright-acknowledged': 'true';
  'community-guidelines-accepted': string;
  // FumsService: per-install device id, and reports queued while offline.
  'fums.dId': string;
  'fums.pending': string[];
}

export type StorageKey = keyof StorageSchema;

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  setItem<K extends StorageKey>(key: K, value: StorageSchema[K]): void {
    if (typeof value === 'string') {
      localStorage.setItem(key, value);
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
  }

  getItem<K extends StorageKey>(key: K): StorageSchema[K] | null {
    const item = localStorage.getItem(key);
    if (!item) return null;

    // Remap bible ids persisted by older app versions to their current ids
    // (the CSB/KJV id migration in issue #201).
    if (key === 'selected-bible-id' || key === 'selected-public-bible-id') {
      return resolveBibleId(item) as StorageSchema[K];
    }

    // Sanitize a stored chat model that is no longer offered (e.g. removed from
    // the lineup) so callers — including any future reader that skips its own
    // validation — never receive an unsupported model id.
    if (key === 'preferred-llm-model') {
      return (
        isValidLlmModel(item) ? item : DEFAULT_LLM_MODEL
      ) as StorageSchema[K];
    }

    // Keep a listed image model, remap retired same-family ids, and fall back
    // to the current default for anything unknown.
    if (key === 'preferred-venice-model') {
      return resolveModel(item).model as StorageSchema[K];
    }

    if (key === 'preferred-color-scheme') {
      return (
        isColorSchemePreference(item) ? item : 'system'
      ) as StorageSchema[K];
    }

    try {
      return JSON.parse(item) as StorageSchema[K];
    } catch {
      return item as StorageSchema[K];
    }
  }

  removeItem(key: StorageKey): void {
    localStorage.removeItem(key);
  }

  clear(): void {
    localStorage.clear();
  }
}
